import useSWR from 'swr';
import { fetcher } from '../../services/fetcher';
import { moduleConfig } from './config';
import { useClients } from '../clients/useData';

export interface Call {
  clientCallId: number;
  clientId?: number;
  calledBy?: number;
  clientCallTypeId?: number;
  clientCallDate?: string;
  clientCallDescr?: string;
  clientCallNotes?: string;
  clientCallInterested?: number;
  clientCallSendEmail?: boolean;
  clientColor?: string;
  clientCallsCreatedAt?: string;
  clientCallsCreatedBy?: number;
  clientCallsUpdatedAt?: string;
  clientCallsUpdatedBy?: number;
  // Related data from backend
  client?: {
    clientId: number;
    clientName?: string;
    clientEmail?: string;
    clientPhone?: string;
  };
  calledByUser?: {
    id: number;
    userFirstName?: string;
    userDisplayName?: string;
  };
  clientCallType?: {
    id: number;
    call_type_desc?: string;
  };
  clientCallsCreatedUser?: {
    userFirstName?: string;
  };
  clientCallsUpdatedUser?: {
    userFirstName?: string;
  };
}

export interface CallsResponse {
  records: Call[];
  thisWeekCount?: number;
  thisMonthCount?: number;
  thisYearCount?: number;
}

export const useCalls = (searchParams?: string, filter?: string) => {
  let queryString = '';
  if (searchParams || filter) {
    const params = new URLSearchParams();
    if (searchParams) params.append('searchParams', searchParams);
    if (filter) params.append('filter', filter);
    queryString = `?${params.toString()}`;
  }
  
  const { data, error, isLoading, mutate } = useSWR<CallsResponse>(
    `${moduleConfig.module.api.list}${queryString}`,
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  // Extract records array from response
  const calls = data?.records || [];

  return {
    data: calls,
    calls: calls,
    total: calls.length,
    isLoading,
    isError: error,
    mutate,
    thisWeekCount: data?.thisWeekCount || 0,
    thisMonthCount: data?.thisMonthCount || 0,
    thisYearCount: data?.thisYearCount || 0,
  };
};

export const useCall = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<Call>(
    id ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    {
      revalidateOnFocus: true,
    }
  );

  return {
    call: data,
    data: data,
    isLoading,
    isError: error,
    mutate,
  };
};

// Hook to get clients for select dropdown
export const useClientsForSelect = () => {
  const { clients, isLoading } = useClients();
  
  return {
    clients: clients || [],
    isLoading,
    options: (clients || []).map((client) => ({
      value: String(client.clientId),
      label: client.clientName || `Client #${client.clientId}`,
    })),
  };
};

// Hook to get users for select dropdown
export const useUsersForSelect = () => {
  const { data, error, isLoading } = useSWR<any[]>(
    '/users',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    users: data || [],
    isLoading,
    isError: error,
    options: (data || []).map((user) => ({
      value: String(user.id),
      label: user.userDisplayName || `${user.userFirstName || ''} ${user.userLastName || ''}`.trim() || `User #${user.id}`,
    })),
  };
};

// Hook to get call types for select dropdown
export const useCallTypesForSelect = () => {
  const { data, error, isLoading } = useSWR<any[]>(
    '/call-type',
    fetcher,
    {
      revalidateOnFocus: false,
    }
  );

  return {
    callTypes: data || [],
    isLoading,
    isError: error,
    options: (data || []).map((callType) => ({
      value: String(callType.id),
      label: callType.call_type_desc || `Call Type #${callType.id}`,
    })),
  };
};

